// Contas da tela da Gestão Comercial (#6496) e do card de energia da Diretoria.
//
// - Energia por canal: o kWh aprovado aberto por canal (Comercial, Parceiros,
//   Empresa Parceira, Licitação), pela mesma regra de canal do
//   Painel Executivo (`canalDaNegociacao`). Pedido do Felipe em 28/10/2026: a
//   licitação conta no total, mas a tela mostra de onde veio cada parte.
// - Motivos de perda em quantidade e kWh: kWh real quando a negociação tem consumo;
//   nos demais, estimado pela faixa da conta de luz do formulário (decisão de
//   27/10/2026, comentário na #6496).
// - Motivos de reprovação da análise (#6428), em quantidade e kWh.
//
// Lógica pura.

import type { Profile } from '../../../types';
import { canalDaNegociacao, type DepartamentoComercial } from '../dashboard/departamentoComercial.ts';

// ── Canais ──

export type Canal = Exclude<DepartamentoComercial, 'todos'>;

/** Ordem e nome dos canais, os mesmos do filtro "Canal de venda" do Painel Executivo. */
export const CANAIS: { id: Canal; rotulo: string }[] = [
  { id: 'comercial', rotulo: 'Comercial' },
  { id: 'parceiros', rotulo: 'Parceiros' },
  { id: 'empresa_parceira', rotulo: 'Empresa Parceira' },
  { id: 'licitacao', rotulo: 'Licitação' },
];

export const rotuloCanal = (c: Canal): string => CANAIS.find((x) => x.id === c)?.rotulo ?? c;

/** Separa as negociações por canal, todos os canais presentes (vazios inclusive). */
export function separarPorCanal<T extends { origem?: string | null; responsavel_id?: string | null }>(
  rows: readonly T[],
  profilesById: ReadonlyMap<string, Profile>,
): Map<Canal, T[]> {
  const out = new Map<Canal, T[]>(CANAIS.map((c) => [c.id, []]));
  for (const r of rows) out.get(canalDaNegociacao(r, profilesById) as Canal)!.push(r);
  return out;
}

export interface ParteDoCanal {
  canal: Canal;
  rotulo: string;
  kwh: number;
  contratos: number;
}

/**
 * Uma linha por canal com kWh no período atual ou no anterior, na ordem fixa.
 * `contar` devolve o aprovado de uma lista (a tela passa o funil do CRM).
 */
export function energiaPorCanal<T>(
  porCanal: ReadonlyMap<Canal, T[]>,
  contar: (rows: T[]) => { qtd: number; kwh: number },
): ParteDoCanal[] {
  return CANAIS.map(({ id, rotulo }) => {
    const { qtd, kwh } = contar(porCanal.get(id) ?? []);
    return { canal: id, rotulo, kwh, contratos: qtd };
  });
}

/**
 * Projeção do mês no ritmo de até hoje: 9 mil kWh no dia 15 de 30 → 18 mil.
 * Sem dias corridos (fração 0) não há ritmo.
 */
export function projecaoDoMes(atual: number, fracao: number): number | null {
  if (!Number.isFinite(atual) || !(fracao > 0)) return null;
  return fracao >= 1 ? atual : atual / fracao;
}

// ── Perdas: kWh real ou estimado pela faixa da conta ──

export type FaixaConta = 'menos_de_r$300' | 'de_r$300_a_r$700' | 'acima_de_r$700';

export const FAIXAS_CONTA: { id: FaixaConta; rotulo: string; meio: number }[] = [
  { id: 'menos_de_r$300', rotulo: 'Menos de R$ 300', meio: 150 },
  { id: 'de_r$300_a_r$700', rotulo: 'De R$ 300 a R$ 700', meio: 500 },
  { id: 'acima_de_r$700', rotulo: 'Acima de R$ 700', meio: 700 },
];

/** Abaixo disso a mediana não representa a faixa: usa o meio da faixa ÷ tarifa. */
export const MIN_AMOSTRAS_FAIXA = 5;

export interface EstimativaFaixa {
  kwh: number;
  /** mediana: do consumo real das negociações da faixa. tarifa: meio da faixa ÷ tarifa vigente. */
  base: 'mediana' | 'tarifa';
  amostras: number;
}

const mediana = (v: number[]): number => {
  const o = [...v].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m] : (o[m - 1] + o[m]) / 2;
};

/**
 * Consumo típico de cada faixa da conta. A amostra são as negociações com faixa e
 * consumo informados (em 27/10/2026: R$ 300–700 ≈ 360 kWh, acima de R$ 700 ≈ 1.200
 * kWh). A faixa sem amostra suficiente usa o meio da faixa ÷ a tarifa vigente.
 */
export function estimativasPorFaixa(
  amostras: readonly { faixa: string | null; kwh: number | null }[],
  tarifa: number | null,
): Partial<Record<FaixaConta, EstimativaFaixa>> {
  const out: Partial<Record<FaixaConta, EstimativaFaixa>> = {};
  for (const f of FAIXAS_CONTA) {
    const valores = amostras.filter((a) => a.faixa === f.id && (a.kwh ?? 0) > 0).map((a) => Number(a.kwh));
    if (valores.length >= MIN_AMOSTRAS_FAIXA) out[f.id] = { kwh: Math.round(mediana(valores)), base: 'mediana', amostras: valores.length };
    else if (tarifa && tarifa > 0) out[f.id] = { kwh: Math.round(f.meio / tarifa), base: 'tarifa', amostras: valores.length };
  }
  return out;
}

export interface MotivoAgrupado {
  motivo: string;
  qtd: number;
  /** kWh das negociações com consumo informado. */
  kwhReal: number;
  /** kWh estimado pela faixa da conta (só nas perdas). */
  kwhEstimado: number;
  /** Sem consumo e sem faixa: fora do kWh. */
  semConsumo: number;
}

export interface Agrupamento {
  motivos: MotivoAgrupado[];
  total: Omit<MotivoAgrupado, 'motivo'>;
}

function agrupar(
  linhas: readonly { motivo: string | null; kwh: number | null; faixa?: string | null }[],
  estimativas: Partial<Record<FaixaConta, EstimativaFaixa>>,
  semMotivo: string,
): Agrupamento {
  const porMotivo = new Map<string, MotivoAgrupado>();
  const total = { qtd: 0, kwhReal: 0, kwhEstimado: 0, semConsumo: 0 };
  for (const l of linhas) {
    const chave = (l.motivo || '').trim() || semMotivo;
    const m = porMotivo.get(chave) ?? { motivo: chave, qtd: 0, kwhReal: 0, kwhEstimado: 0, semConsumo: 0 };
    m.qtd++; total.qtd++;
    const kwh = Number(l.kwh);
    const est = l.faixa ? estimativas[l.faixa as FaixaConta] : undefined;
    if (kwh > 0) { m.kwhReal += kwh; total.kwhReal += kwh; }
    else if (est) { m.kwhEstimado += est.kwh; total.kwhEstimado += est.kwh; }
    else { m.semConsumo++; total.semConsumo++; }
    porMotivo.set(chave, m);
  }
  const motivos = [...porMotivo.values()].sort((a, b) =>
    b.qtd - a.qtd || (b.kwhReal + b.kwhEstimado) - (a.kwhReal + a.kwhEstimado) || a.motivo.localeCompare(b.motivo, 'pt-BR'));
  return { motivos, total };
}

/** Perdas do período por motivo, com kWh real e estimado pela faixa da conta. */
export function agruparPerdas(
  linhas: readonly { motivo: string | null; kwh: number | null; faixa: string | null }[],
  estimativas: Partial<Record<FaixaConta, EstimativaFaixa>>,
): Agrupamento {
  return agrupar(linhas, estimativas, 'Não informado');
}

/**
 * Reprovações da análise no período, por motivo. Uma negociação reanalisada e
 * reprovada de novo conta uma vez, pela decisão mais recente.
 */
export function agruparReprovacoes(
  linhas: readonly { negociacao_id: number; motivo: string | null; kwh: number | null; decididoEm: string | null }[],
): Agrupamento {
  const ultima = new Map<number, (typeof linhas)[number]>();
  for (const l of linhas) {
    const atual = ultima.get(l.negociacao_id);
    if (!atual || String(l.decididoEm ?? '') > String(atual.decididoEm ?? '')) ultima.set(l.negociacao_id, l);
  }
  return agrupar([...ultima.values()], {}, 'Sem motivo registrado');
}

// ── Resultado por vendedor e canal ──

export interface VendedorDoMes {
  id: string;
  nome: string;
  canal: Canal;
  /** Aprovadas no mês (base da comissão). */
  aprovadas: number;
  kwhAprovado: number;
  assinados: number;
  emAssinatura: number;
  /** Meta de kWh do mês (Folha de Pagamento). null/0 = sem meta. */
  metaKwh: number | null;
}

export interface CanalDoMes {
  canal: Canal;
  rotulo: string;
  pessoas: (VendedorDoMes & { atingimento: number | null })[];
  aprovadas: number;
  kwhAprovado: number;
  /** Soma das metas de quem tem meta. */
  metaKwh: number;
  atingimento: number | null;
}

const atingir = (kwh: number, meta: number | null | undefined) => (meta && meta > 0 ? kwh / meta : null);

/** Agrupa as pessoas por canal, com o total e o % da meta de cada um. Só canais com alguém. */
export function resultadoPorCanal(pessoas: readonly VendedorDoMes[]): CanalDoMes[] {
  return CANAIS.flatMap(({ id, rotulo }) => {
    const doCanal = pessoas
      .filter((p) => p.canal === id)
      .map((p) => ({ ...p, atingimento: atingir(p.kwhAprovado, p.metaKwh) }))
      .sort((a, b) => b.kwhAprovado - a.kwhAprovado || b.assinados - a.assinados || a.nome.localeCompare(b.nome, 'pt-BR'));
    if (doCanal.length === 0) return [];
    const kwhAprovado = doCanal.reduce((t, p) => t + p.kwhAprovado, 0);
    const metaKwh = doCanal.reduce((t, p) => t + (p.metaKwh && p.metaKwh > 0 ? p.metaKwh : 0), 0);
    return [{
      canal: id, rotulo, pessoas: doCanal,
      aprovadas: doCanal.reduce((t, p) => t + p.aprovadas, 0),
      kwhAprovado, metaKwh, atingimento: atingir(kwhAprovado, metaKwh),
    }];
  });
}

// ── Hoje ──

/** Dias inteiros desde a data (0 se vazia ou no futuro). */
export function diasDesde(iso: string | null | undefined, agora: Date): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((agora.getTime() - t) / 86_400_000));
}
